import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/roles";
import { fmtHM, fmtTime, fmtDate, todayStr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CheckCircle2, Clock, Trash2, Building2, GraduationCap, Link2, Pause, Play, CalendarDays, Timer, Link as LinkIcon, ClipboardList, Download, Pencil, MapPin, Building, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";
import { weeklyOffLabel, WEEKDAYS, OCCURRENCES } from "@/lib/weeklyOff";
import { getTrackerOptionsForDepartment } from "@/lib/trackerOptions";

const CATEGORY_KEYS = ["Video & Photography Production", "Graphic Production", "Content Production"];
const CAT_SHORT = { "Video & Photography Production": "Video & Photo", "Graphic Production": "Graphic", "Content Production": "Content" };

function istNow() { return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })); }
function monthNow() { const d = istNow(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }
function shiftDateStr(dstr, days) { const d = new Date(dstr + "T00:00:00"); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }
function weekDaysOf(dstr) {
  const d = new Date(dstr + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  const mon = new Date(d); mon.setDate(d.getDate() - day);
  return Array.from({ length: 7 }, (_, i) => { const x = new Date(mon); x.setDate(mon.getDate() + i); return x.toISOString().slice(0, 10); });
}
function monthBounds(m) {
  const [y, mo] = m.split("-").map(Number);
  const start = `${m}-01`;
  const end = new Date(y, mo, 0).toISOString().slice(0, 10);
  return { start, end, days: new Date(y, mo, 0).getDate(), y, mo };
}
function istTodayStr() { const d = istNow(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

const emptyForm = {
  presence: "present",
  date: "",
  location: "Office", // 'Office' | 'Field' | 'Work From Home'
  field_type: "School", // 'School' | 'Event' | 'Other Field Work'
  school: "",
  task_source: "direct_task", // 'linked_asset' | 'direct_task'
  linked_type: "", // 'Creative Asset' | 'Social Media' | 'School / Client' | 'Sales Pipeline'
  linked_asset_id: "none",
  work_category: "",
  sub_category: "",
  direct_task_type: "",
  direct_task_note: "",
  activity: "",
  outcome: "",
  next_action: "",
  next_action_due: "",
  leave_reason: "",
  on_behalf_user_id: "me",
};

function StatusBadge({ s }) {
  const map = {
    completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    leave: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  };
  const label = { completed: "Completed", in_progress: "In Progress", leave: "Leave" }[s] || s;
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${map[s] || "bg-muted"}`}>{label}</span>;
}

function AttendanceBadge({ s }) {
  if (!s) return null;
  const late = s === "LATE";
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${late ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"}`}>{s}</span>;
}

function GpsBadge({ att, privileged }) {
  if (!att || att.location !== "Office") return null; // GPS is captured only for Office check-ins
  const notVerified = !att.gps_status || att.gps_status === "not_verified" || att.gps_status === "not_captured";
  const ok = att.gps_status === "verified";
  // Map link + exact distance are shown ONLY in authorized Admin/Manager views.
  const dist = privileged && att.gps_distance_m != null
    ? (att.gps_distance_m < 1000 ? `${Math.round(att.gps_distance_m)} m` : `${(att.gps_distance_m / 1000).toFixed(1)} km`)
    : null;
  if (notVerified) {
    return (
      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        title="Location was not shared for this check-in" data-testid="gps-badge">Not verified</span>
    );
  }
  return (
    <a
      href={privileged && att.gps_lat != null ? `https://www.google.com/maps/search/?api=1&query=${att.gps_lat},${att.gps_lng}` : undefined}
      target={privileged && att.gps_lat != null ? "_blank" : undefined}
      rel="noreferrer"
      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold transition-opacity ${ok ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"} ${privileged && att.gps_lat != null ? "hover:opacity-80 underline decoration-dotted" : "pointer-events-none"}`}
      title={privileged && att.gps_lat != null ? "View exact check-in location on Google Maps" : undefined}
      data-testid="gps-badge"
    >
      {ok ? `GPS ✓ Office${dist ? ` · ${dist}` : ""}` : `Outside office${dist ? ` · ${dist} away` : ""}`}
    </a>
  );
}

export default function DailyTracker() {
  const { user, viewMode } = useAuth();
  const admin = isAdmin(user) && viewMode !== "employee";
  const [tab, setTab] = useState("my"); // 'my' | 'team-day' | 'team-month' | 'team-report'
  const [myView, setMyView] = useState("day"); // 'day' | 'week' | 'month'
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [weekAnchor, setWeekAnchor] = useState(todayStr());
  const [month, setMonth] = useState(monthNow());
  const [teamDate, setTeamDate] = useState(todayStr());

  const [logs, setLogs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [config, setConfig] = useState({ subcategories: {}, school_master: [], work_locations: ["Office", "Field"], direct_task_types: [] });
  const [holidays, setHolidays] = useState([]);
  const [weeklyOffs, setWeeklyOffs] = useState([]);
  const [open, setOpen] = useState(false);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [hForm, setHForm] = useState({ date: "", name: "" });
  const [unlockForm, setUnlockForm] = useState({ user_id: "", date: "", reason: "" });
  const [form, setForm] = useState(emptyForm);
  const [now, setNow] = useState(Date.now());
  const [pending, setPending] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [progressLog, setProgressLog] = useState(null);
  const [progressMode, setProgressMode] = useState("update");
  const [progressForm, setProgressForm] = useState({ activity: "", outcome: "", next_action: "", next_action_due: "", location: "Office", school: "" });
  const [leaveBal, setLeaveBal] = useState(null);
  const [myUnlocks, setMyUnlocks] = useState([]);
  const [report, setReport] = useState(null);
  const [reportFilters, setReportFilters] = useState({ user_id: "all", department: "all", reporting_to: "all" });
  const [reportDrill, setReportDrill] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(() => {
    api.get("/timelogs", { params: { month } }).then((r) => setLogs(r.data || []));
  }, [month]);

  const loadRange = useCallback(() => {
    api.get("/timelogs", { params: { month } }).then((r) => setLogs(r.data || []));
  }, [month]);

  const loadLeaveBalance = useCallback(() => {
    const target = admin && form.on_behalf_user_id && form.on_behalf_user_id !== "me" ? form.on_behalf_user_id : user?.user_id;
    if (!target) return;
    api.get("/tracker/leave-balance", { params: { month, user_id: target } })
      .then((r) => setLeaveBal(r.data))
      .catch(() => setLeaveBal(null));
  }, [admin, form.on_behalf_user_id, month, user?.user_id]);

  const loadUnlocks = useCallback(() => {
    if (!user) return;
    api.get("/tracker/my-unlocks").then((r) => setMyUnlocks((r.data || []).map((u) => u.date))).catch(() => setMyUnlocks([]));
  }, [user]);

  const loadReport = useCallback(() => {
    if (!admin && !user?.has_team) return;
    const params = { month };
    if (reportFilters.user_id !== "all") params.user_id = reportFilters.user_id;
    if (reportFilters.department !== "all") params.department = reportFilters.department;
    if (reportFilters.reporting_to !== "all") params.reporting_to = reportFilters.reporting_to;
    api.get("/tracker/report", { params }).then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [admin, user?.has_team, month, reportFilters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRange(); }, [loadRange]);
  useEffect(() => { loadUnlocks(); }, [loadUnlocks]);
  useEffect(() => { if (tab === "team-report") loadReport(); }, [tab, loadReport]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data || {}));
    api.get("/assets").then((r) => setAssets(r.data || []));
    api.get("/access/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/tracker/holidays").then((r) => setHolidays(r.data || []));
    api.get("/tracker/weekly-offs").then((r) => setWeeklyOffs(r.data?.rules || []));
    api.get("/schools", { params: { active_only: true } }).then((r) => setSchools(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && form.presence === "leave") loadLeaveBalance();
  }, [open, form.presence, form.on_behalf_user_id, loadLeaveBalance]);

  const activeAssets = useMemo(() => assets.filter((a) => !["Cancelled", "Archived"].includes(a.status)), [assets]);
  const activeSchools = useMemo(() => {
    if (schools.length > 0) return schools.filter((s) => s.status === "active");
    return (config.school_master || []).map((name) => ({ name, school_id: name }));
  }, [schools, config.school_master]);

  // Target employee for the current form (self or on-behalf)
  const targetEmployee = useMemo(() => {
    const onBehalf = admin && form.on_behalf_user_id && form.on_behalf_user_id !== "me" ? form.on_behalf_user_id : user?.user_id;
    return users.find((u) => u.user_id === onBehalf) || user;
  }, [admin, form.on_behalf_user_id, users, user]);

  // Department-specific options
  const deptTrackerOptions = useMemo(() => {
    return getTrackerOptionsForDepartment(targetEmployee?.department, targetEmployee?.roles);
  }, [targetEmployee?.department, targetEmployee?.roles]);

  // Set intelligent defaults for task_source based on department
  useEffect(() => {
    if (deptTrackerOptions.linkedWork.length === 0 && form.task_source === "linked_asset") {
      setForm((f) => ({ ...f, task_source: "direct_task" }));
    }
  }, [deptTrackerOptions]);

  const liveSec = (l) => {
    let s = l.active_seconds || 0;
    if (l.is_running && l.timer_started_at) {
      const elapsed = Math.floor((now - new Date(l.timer_started_at).getTime()) / 1000);
      if (elapsed > 0) s += elapsed;
    }
    return s;
  };

  const today = istTodayStr();

  // Validate the current form and build a staged entry (does NOT save yet).
  const buildEntry = () => {
    const onBehalf = admin && form.on_behalf_user_id && form.on_behalf_user_id !== "me" ? form.on_behalf_user_id : null;
    const onBehalfName = onBehalf ? (users.find((u) => u.user_id === onBehalf)?.name || "team member") : null;
    if (form.presence === "leave") {
      const isExcess = leaveBal && leaveBal.remaining <= 0;
      return {
        id: crypto.randomUUID(), kind: "leave",
        label: `Leave${form.leave_reason ? ` — ${form.leave_reason}` : ""}${isExcess ? " (excess — needs approval)" : ""}${onBehalfName ? ` · for ${onBehalfName}` : ""}`,
        payload: { presence: "leave", date: form.date || today, on_behalf_user_id: onBehalf, excess: !!isExcess, leave_reason: form.leave_reason },
        form: { ...form },
      };
    }

    if (form.location === "Field" && form.field_type === "School" && !form.school) {
      toast.error("Please select an active School.");
      return null;
    }

    if (form.task_source === "linked_asset") {
      if (form.linked_asset_id === "none") { toast.error("Select a Linked Work Item, or switch to Direct Work."); return null; }
    } else if (!form.direct_task_type) {
      toast.error("Please select a Direct Work type."); return null;
    }

    const note = (form.direct_task_note || "").trim();
    const payload = {
      presence: "present",
      date: form.date || today,
      location: form.location,
      field_type: form.location === "Field" ? form.field_type : null,
      school: form.location === "Field" && form.field_type === "School" ? form.school : (form.location === "Field" ? form.field_type : null),
      task_source: form.task_source,
      linked_type: form.linked_type,
      direct_task_type: form.task_source === "direct_task" ? form.direct_task_type : null,
      work_category: form.work_category,
      sub_category: form.sub_category,
      linked_asset_id: form.task_source === "linked_asset" && form.linked_asset_id !== "none" ? form.linked_asset_id : null,
      activity: form.task_source === "direct_task" && note ? `${form.direct_task_type} — ${note}` : (form.direct_task_type || null),
      outcome: null,
      next_action: (form.next_action || "").trim() || null,
      next_action_due: form.next_action_due || null,
      on_behalf_user_id: onBehalf,
    };
    const asset = assets.find((a) => a.asset_id === form.linked_asset_id);
    const locLabel = form.location === "Field" ? `Field (${form.field_type === "School" ? (form.school || "School") : form.field_type})` : form.location;
    const label = form.task_source === "linked_asset"
      ? `${asset ? `${asset.work_item_id} — ${asset.work_item || asset.title}` : "Linked Work"} · ${locLabel}${onBehalfName ? ` · for ${onBehalfName}` : ""}`
      : `${form.direct_task_type}${note ? ` — ${note}` : ""} · ${locLabel}${onBehalfName ? ` · for ${onBehalfName}` : ""}`;
    return { id: crypto.randomUUID(), kind: "activity", label, payload, form: { ...form } };
  };

  const resetTaskFields = (f) => ({
    ...f,
    work_category: "",
    sub_category: "",
    linked_asset_id: "none",
    direct_task_type: "",
    direct_task_note: "",
    activity: "",
    outcome: "",
    next_action: "",
    next_action_due: "",
    leave_reason: "",
  });

  const addAnother = () => {
    const entry = buildEntry();
    if (!entry) return;
    setPending((p) => [...p, entry]);
    setForm(resetTaskFields);
  };

  const formHasContent = form.presence === "leave" || (form.task_source === "linked_asset" ? form.linked_asset_id !== "none" : !!form.direct_task_type);

  const openReview = () => {
    let next = pending;
    if (formHasContent) {
      const entry = buildEntry();
      if (!entry) return;
      next = [...pending, entry];
      setPending(next);
      setForm(resetTaskFields);
    }
    if (next.length === 0) { toast.error("Nothing to save — add a task or leave first."); return; }
    setReviewOpen(true);
  };

  const editEntry = (id) => {
    const entry = pending.find((e) => e.id === id);
    if (!entry) return;
    setPending((p) => p.filter((e) => e.id !== id));
    setForm(entry.form);
    setReviewOpen(false);
  };

  const removeEntry = (id) => setPending((p) => p.filter((e) => e.id !== id));

  const confirmSaveAll = async () => {
    setSavingAll(true);
    try {
      // One-time GPS capture: only for the FIRST OFFICE log of the day.
      let gps = null;
      const firstPresent = pending.find((e) => e.kind === "activity");
      if (!hasAttendance && firstPresent && firstPresent.payload.location === "Office" && "geolocation" in navigator) {
        gps = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          );
        });
      }
      for (const entry of pending) {
        const payload = gps && entry === firstPresent ? { ...entry.payload, gps } : entry.payload;
        await api.post("/tracker", payload);
      }
      const n = pending.length;
      const hasLeave = pending.some((e) => e.kind === "leave");
      toast.success(n === 1 ? "Saved — timer started" : `${n} entries saved`);
      setPending([]); setReviewOpen(false); setOpen(false); setForm(emptyForm);
      load(); if (hasLeave) loadLeaveBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save all entries — please review and try again");
    } finally { setSavingAll(false); }
  };

  const targetUid = admin && form.on_behalf_user_id && form.on_behalf_user_id !== "me" ? form.on_behalf_user_id : user?.user_id;
  const formDate = form.date || today;
  const hasAttendance = logs.some((l) => l.user_id === targetUid && l.date === formDate && (l.record_type === "attendance" || l.presence === "present") && l.presence !== "leave");

  const unlockDate = async () => {
    if (!unlockForm.user_id || !unlockForm.date || !unlockForm.reason.trim()) return toast.error("Employee, date and reason are required");
    try {
      await api.post("/tracker/unlock-date", unlockForm);
      toast.success("Date unlocked for the employee");
      setUnlockOpen(false); setUnlockForm({ user_id: "", date: "", reason: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unlock date");
    }
  };

  const pauseResume = async (l) => {
    try {
      if (l.is_running) {
        await api.post(`/tracker/${l.log_id}/pause`);
      } else {
        await api.post(`/tracker/${l.log_id}/resume`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update timer"); }
  };

  const openProgress = (l, mode) => {
    setProgressLog(l);
    setProgressMode(mode);
    setProgressForm({
      activity: l.activity || "",
      outcome: l.outcome || "",
      next_action: l.next_action || "",
      next_action_due: l.next_action_due || "",
      location: l.location || "Office",
      school: l.school || "",
    });
  };

  const saveProgress = async () => {
    if (!progressLog) return;
    try {
      await api.patch(`/tracker/${progressLog.log_id}/progress`, progressForm);
      toast.success("Activity updated");
      setProgressLog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update activity"); }
  };

  const confirmComplete = async () => {
    if (!progressLog) return;
    if (!progressForm.outcome.trim()) return toast.error("Outcome / What Was Achieved is required to complete this activity.");
    try {
      await api.patch(`/tracker/${progressLog.log_id}/progress`, progressForm);
      await api.post(`/tracker/${progressLog.log_id}/complete`, { outcome: progressForm.outcome });
      toast.success("Activity marked as Completed");
      setProgressLog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete activity"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api.delete(`/timelogs/${id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const saveHoliday = async () => {
    if (!hForm.date || !hForm.name.trim()) return toast.error("Date and Name are required");
    try {
      await api.post("/tracker/holidays", hForm);
      toast.success("Holiday added");
      setHolidayOpen(false); setHForm({ date: "", name: "" });
      api.get("/tracker/holidays").then((r) => setHolidays(r.data || []));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add holiday"); }
  };

  const deleteHoliday = async (d) => {
    if (!window.confirm("Remove this holiday?")) return;
    try {
      await api.delete(`/tracker/holidays/${d}`);
      toast.success("Removed");
      api.get("/tracker/holidays").then((r) => setHolidays(r.data || []));
    } catch (e) { toast.error("Could not remove holiday"); }
  };

  const exportReport = () => {
    const params = new URLSearchParams({ month });
    if (reportFilters.user_id !== "all") params.append("user_id", reportFilters.user_id);
    if (reportFilters.department !== "all") params.append("department", reportFilters.department);
    if (reportFilters.reporting_to !== "all") params.append("reporting_to", reportFilters.reporting_to);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/timelogs/export?${params.toString()}`, "_blank");
  };

  const mine = logs.filter((l) => l.user_id === user?.user_id);
  const dayLogs = mine.filter((l) => l.date === selectedDate);
  const dayAtt = dayLogs.find((l) => l.record_type === "attendance") || null;
  const dayActs = dayLogs.filter((l) => l.record_type !== "attendance");
  const holidaySet = new Set(holidays.map((h) => h.date));
  const offLabel = (d) => {
    const h = holidays.find((x) => x.date === d);
    if (h) return h.name;
    return weeklyOffLabel(d, weeklyOffs);
  };

  const summaryOf = (rows) => {
    const present = rows.filter((l) => l.presence !== "leave" && l.record_type !== "attendance");
    return {
      total: present.length,
      leave: rows.filter((l) => l.presence === "leave").length,
      completed: present.filter((l) => l.status === "completed").length,
      inProgress: present.filter((l) => l.status === "in_progress").length,
      office: present.filter((l) => l.location === "Office").length,
      school: present.filter((l) => l.location === "Field" || l.location === "School").length,
      wfh: present.filter((l) => l.location === "Work From Home" || l.location === "WFH").length,
      seconds: present.reduce((acc, l) => acc + liveSec(l), 0),
    };
  };

  const sum = summaryOf(mine);
  const daySum = summaryOf(dayLogs);

  const Stat = ({ label, value, testid }) => (
    <div data-testid={testid} className="rounded-xl border border-border bg-card px-3.5 py-3">
      <p className="text-xl font-bold font-display">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );

  const LogRow = ({ l, showUser, canComplete }) => (
    <tr data-testid={`log-row-${l.log_id}`} className="border-b border-border last:border-0 hover:bg-muted/20">
      <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(l.date)}</td>
      {showUser && <td className="px-3 py-2.5">{l.user_name}</td>}
      <td className="px-3 py-2.5">{l.presence === "leave" ? <span className="text-amber-600 font-semibold">Leave</span> : "Present"}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        {l.presence === "leave" ? "—" : (
          <span className="inline-flex items-center gap-1 text-xs font-medium">
            {l.location === "Office" && <Building className="h-3 w-3 text-blue-600" />}
            {l.location === "Field" && <MapPin className="h-3 w-3 text-violet-600" />}
            {(l.location === "Work From Home" || l.location === "WFH") && <HomeIcon className="h-3 w-3 text-teal-600" />}
            {l.location}{l.school ? ` · ${l.school}` : ""}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5">{l.task_source === "direct_task" ? <span className="inline-flex items-center gap-1 text-violet-600 text-xs font-semibold"><ClipboardList className="h-3 w-3" />Direct</span> : <span className="inline-flex items-center gap-1 text-blue-600 text-xs font-semibold"><LinkIcon className="h-3 w-3" />Linked</span>}</td>
      <td className="px-3 py-2.5 max-w-[240px]">
        <p className="font-medium text-xs truncate">{l.presence === "leave" ? (l.leave_type || "Leave") : (l.activity || l.direct_task_type || "—")}</p>
        {l.outcome && <p className="text-[11px] text-muted-foreground truncate mt-0.5 leading-tight">↳ {l.outcome}</p>}
        {l.next_action && <p className="text-[10px] text-primary truncate mt-0.5">Next: {l.next_action}{l.next_action_due ? ` (due ${fmtDate(l.next_action_due)})` : ""}</p>}
        {(l.carried_days || 0) > 0 && <span className="text-[10px] text-violet-600 font-semibold">· CF {l.carried_days}d</span>}
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">{l.task_source === "direct_task" ? (l.direct_task_type || "—") : (l.linked_asset_label || l.linked_type || "—")}</td>
      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{l.start_time ? fmtTime(l.start_time) : "—"}</td>
      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{l.completed_at ? fmtTime(l.completed_at) : "—"}</td>
      <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs">{l.status === "leave" ? "—" : fmtHM(liveSec(l))}</td>
      <td className="px-3 py-2.5"><StatusBadge s={l.status} /></td>
      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        {canComplete && l.status === "in_progress" && (
          <button data-testid={`update-${l.log_id}`} onClick={() => openProgress(l, "update")} className="mr-1 p-1 text-muted-foreground hover:text-foreground" title="Update Activity & Outcomes">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {canComplete && l.status === "in_progress" && l.user_id === user?.user_id && (
          <button data-testid={`toggle-timer-${l.log_id}`} onClick={() => pauseResume(l)} className="mr-1 p-1 text-muted-foreground hover:text-foreground" title={l.is_running ? "Pause" : "Resume"}>
            {l.is_running ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
        )}
        {canComplete && l.status === "in_progress" && <Button data-testid={`complete-${l.log_id}`} size="sm" className="h-7 text-xs gap-1" onClick={() => openProgress(l, "complete")}><CheckCircle2 className="h-3.5 w-3.5" /> Complete</Button>}
        {canComplete && l.user_id === user?.user_id && <button data-testid={`delete-${l.log_id}`} onClick={() => remove(l.log_id)} className="ml-2 p-1 text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
      </td>
    </tr>
  );

  const TableHead = ({ showUser }) => (
    <thead>
      <tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
        {["Date", showUser && "Employee", "Presence", "Location", "Source", "Activity / Outcome", "Type", "Started", "Completed", "Active", "Status", ""].filter(Boolean).map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
      </tr>
    </thead>
  );

  const { days, y, mo } = monthBounds(month);
  const cellFor = (uid, day) => {
    const dstr = `${month}-${String(day).padStart(2, "0")}`;
    const rows = logs.filter((l) => l.user_id === uid && l.date === dstr);
    if (rows.length === 0) return holidaySet.has(dstr) ? "H" : (weeklyOffLabel(dstr, weeklyOffs) ? "W" : null);
    if (rows.some((r) => r.presence === "leave")) return "L";
    if (rows.some((r) => r.location === "Field" || r.location === "School")) return "S";
    if (rows.some((r) => r.location === "Work From Home" || r.location === "WFH")) return "R";
    if (rows.some((r) => r.location === "Office")) return "O";
    return "✓";
  };
  const cellColor = { L: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300", S: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300", O: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300", R: "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300", H: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300", W: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300", "✓": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" };
  const activeUsers = users.filter((u) => u.status === "active");
  const departments = Array.from(new Set(activeUsers.map((u) => u.department).filter(Boolean)));

  return (
    <div className="fade-up space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {/* GM read-only visibility: ADMIN sees team tabs in BOTH views */}
          {(admin || isAdmin(user)) && (
            <div className="flex rounded-lg border border-border p-0.5 flex-wrap">
              {[["my", "My Tracker"], ["team-day", "Team · Day"], ["team-month", "Team · Month"], ["team-report", "Report"]].map(([k, lbl]) => (
                <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-1.5 text-sm rounded-md ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{lbl}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {admin && (
            <>
              <Button data-testid="unlock-date-btn" variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={() => setUnlockOpen(true)}><Clock className="h-3.5 w-3.5" /> Unlock Date</Button>
              <Button data-testid="manage-holidays-btn" variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={() => setHolidayOpen(true)}><CalendarDays className="h-3.5 w-3.5" /> Holidays ({holidays.length})</Button>
            </>
          )}

          {/* "+ Log Daily Activity & Attendance" */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="log-activity-button" className="gap-1.5"><Plus className="h-4 w-4" /> Log Daily Activity &amp; Attendance</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
              <DialogHeader><DialogTitle className="font-display">Log Daily Activity &amp; Attendance</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {/* Admin on-behalf selector */}
                {admin && (
                  <div>
                    <Label>Log on behalf of</Label>
                    <Select value={form.on_behalf_user_id} onValueChange={(v) => setForm({ ...form, on_behalf_user_id: v })}>
                      <SelectTrigger data-testid="on-behalf-select" className="mt-1"><SelectValue placeholder="Myself" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="me">Myself ({user?.name})</SelectItem>
                        {users.filter((u) => u.status === "active" && u.user_id !== user?.user_id).map((u) => (
                          <SelectItem key={u.user_id} value={u.user_id}>{u.name}{u.department ? ` (${u.department})` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Presence: Present / Leave */}
                <div>
                  <div className="flex rounded-lg border border-border p-0.5">
                    {[["present", "Present"], ["leave", "Leave"]].map(([k, lbl]) => (
                      <button key={k} data-testid={`presence-${k}`} onClick={() => setForm({ ...form, presence: k })} className={`flex-1 py-1.5 text-sm rounded-md ${form.presence === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>

                {form.presence === "leave" ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-center" data-testid="leave-entitlement">
                        <p className="font-display font-extrabold text-xl">{leaveBal ? leaveBal.entitlement : 2}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Entitlement</p>
                      </div>
                      <div className="rounded-lg border border-border bg-card px-3 py-2.5 text-center" data-testid="leave-used">
                        <p className="font-display font-extrabold text-xl">{leaveBal ? leaveBal.used : "—"}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Used</p>
                      </div>
                      <div className={`rounded-lg border px-3 py-2.5 text-center ${leaveBal && leaveBal.remaining <= 0 ? "border-rose-300 bg-rose-50/60 dark:bg-rose-950/30" : "border-border bg-card"}`} data-testid="leave-remaining">
                        <p className="font-display font-extrabold text-xl">{leaveBal ? leaveBal.remaining : "—"}</p>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Remaining</p>
                      </div>
                    </div>
                    <div><Label>Date</Label><Input data-testid="leave-date-input" type="date" value={form.date || today} min={admin ? undefined : (myUnlocks.length ? [...myUnlocks].sort()[0] : today)} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
                    <div><Label>Note <span className="text-muted-foreground font-normal">(optional)</span></Label><Textarea data-testid="leave-reason-input" value={form.leave_reason} onChange={(e) => setForm({ ...form, leave_reason: e.target.value })} className="mt-1" rows={2} /></div>
                    {leaveBal && leaveBal.remaining <= 0 ? (
                      <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 p-3" data-testid="excess-leave-notice">
                        <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">No regular leave balance remains for this month.</p>
                        <p className="text-[11px] text-muted-foreground mt-1">You can still submit a <strong>Leave Request — Excess Leave</strong> (paid or LOP decided by admin).</p>
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">Each employee is entitled to 2 regular leave days per month. Marking leave will use 1 day.</p>
                    )}
                  </div>
                ) : (
                  <>
                    {pending.length > 0 && <p className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 font-medium" data-testid="activity-saved-note">{pending.length} {pending.length === 1 ? "entry" : "entries"} staged — not saved yet. Click "Save &amp; Close" to review and confirm.</p>}
                    <div><Label>Date</Label><Input data-testid="activity-date-input" type="date" value={form.date || today} min={admin ? undefined : (myUnlocks.length ? [...myUnlocks].sort()[0] : today)} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>

                    {/* ACTIVITY-LEVEL WORK LOCATION: Office / Field / WFH */}
                    <div className="rounded-xl border border-border p-3.5 bg-muted/20 space-y-3">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Activity Work Location</Label>
                        <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v, school: "" })}>
                          <SelectTrigger data-testid="location-select" className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Office">Office</SelectItem>
                            <SelectItem value="Field">Field</SelectItem>
                            {(targetEmployee?.wfh_allowed || admin) && <SelectItem value="Work From Home" data-testid="location-wfh">WFH (Work From Home)</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Field Breakdown: School / Event / Other Field Work */}
                      {form.location === "Field" && (
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border">
                          <div>
                            <Label className="text-xs">Field Type</Label>
                            <Select value={form.field_type} onValueChange={(v) => setForm({ ...form, field_type: v, school: "" })}>
                              <SelectTrigger data-testid="field-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="School">School</SelectItem>
                                <SelectItem value="Event">Event</SelectItem>
                                <SelectItem value="Other Field Work">Other Field Work</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {form.field_type === "School" && (
                            <div>
                              <Label className="text-xs">Active School</Label>
                              <Select value={form.school} onValueChange={(v) => setForm({ ...form, school: v })}>
                                <SelectTrigger data-testid="school-select" className="mt-1"><SelectValue placeholder="Select active school" /></SelectTrigger>
                                <SelectContent>
                                  {activeSchools.map((s) => (
                                    <SelectItem key={s.school_id || s.name} value={s.name}>
                                      {s.name}{s.city ? ` · ${s.city}` : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Department-specific Task Source & Options */}
                    <div>
                      <div className="flex items-center justify-between">
                        <Label>Work Type</Label>
                        {targetEmployee?.department && (
                          <span className="text-[11px] text-muted-foreground font-medium">{targetEmployee.department}</span>
                        )}
                      </div>

                      {deptTrackerOptions.linkedWork.length > 0 && (
                        <div className="flex rounded-lg border border-border p-0.5 mt-1.5">
                          {[["direct_task", "Direct Work"], ["linked_asset", "Linked Work"]].map(([k, lbl]) => (
                            <button key={k} data-testid={`source-${k}`} onClick={() => setForm({ ...form, task_source: k })} className={`flex-1 py-1.5 text-sm rounded-md ${form.task_source === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{lbl}</button>
                          ))}
                        </div>
                      )}
                    </div>

                    {form.task_source === "linked_asset" && deptTrackerOptions.linkedWork.length > 0 ? (
                      <>
                        <div>
                          <Label>Linked Work Type</Label>
                          <Select value={form.linked_type || deptTrackerOptions.linkedWork[0]} onValueChange={(v) => setForm({ ...form, linked_type: v })}>
                            <SelectTrigger data-testid="linked-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {deptTrackerOptions.linkedWork.map((lw) => <SelectItem key={lw} value={lw}>{lw}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* If Creative Asset */}
                        {(form.linked_type === "Creative Asset" || (!form.linked_type && deptTrackerOptions.linkedWork.includes("Creative Asset"))) && (
                          <div>
                            <Label>Linked Creative Work Item</Label>
                            <Select value={form.linked_asset_id} onValueChange={(v) => setForm({ ...form, linked_asset_id: v })}>
                              <SelectTrigger data-testid="linked-asset-select" className="mt-1"><SelectValue placeholder="Select a work item" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select a work item…</SelectItem>
                                {activeAssets.map((a) => <SelectItem key={a.asset_id} value={a.asset_id}>{a.work_item_id} — {a.work_item || a.title} · {a.current_stage}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* If School / Client */}
                        {form.linked_type === "School / Client" && (
                          <div>
                            <Label>Active School / Client</Label>
                            <Select value={form.school} onValueChange={(v) => setForm({ ...form, school: v })}>
                              <SelectTrigger data-testid="linked-school-select" className="mt-1"><SelectValue placeholder="Select active school" /></SelectTrigger>
                              <SelectContent>
                                {activeSchools.map((s) => <SelectItem key={s.school_id || s.name} value={s.name}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* If Sales Pipeline (future connection only) */}
                        {form.linked_type === "Sales Pipeline" && (
                          <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
                            Sales Pipeline module connection (future). Log your direct sales activity below for now.
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div>
                          <Label>Direct Work Option</Label>
                          <Select value={form.direct_task_type} onValueChange={(v) => setForm({ ...form, direct_task_type: v })}>
                            <SelectTrigger data-testid="direct-task-select" className="mt-1"><SelectValue placeholder="Select work option…" /></SelectTrigger>
                            <SelectContent>
                              {deptTrackerOptions.directWork.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        {form.direct_task_type && (
                          <div>
                            <Label>What is this {form.direct_task_type} about? <span className="text-muted-foreground font-normal">(optional description)</span></Label>
                            <Textarea data-testid="direct-task-note-input" value={form.direct_task_note} onChange={(e) => setForm({ ...form, direct_task_note: e.target.value })} className="mt-1" rows={2} placeholder={`Describe this ${form.direct_task_type.toLowerCase()}`} />
                          </div>
                        )}
                      </>
                    )}

                    {/* Common Activity Fields */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                      <div>
                        <Label className="text-xs">Next Action <span className="text-muted-foreground font-normal">(optional)</span></Label>
                        <Input data-testid="next-action-input" value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} className="mt-1 text-xs" placeholder="Follow-up step…" />
                      </div>
                      <div>
                        <Label className="text-xs">Next Action Due</Label>
                        <Input data-testid="next-action-due-input" type="date" value={form.next_action_due} onChange={(e) => setForm({ ...form, next_action_due: e.target.value })} className="mt-1 text-xs" />
                      </div>
                    </div>

                    <p className="text-[11px] text-muted-foreground">The timer starts automatically. You can update "What Was Completed?", Outcome, and Next Actions anytime from the card. Outcome is required to mark Completed.</p>
                  </>
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button data-testid="submit-add-another-button" variant="outline" onClick={addAnother}>Save &amp; Add Another Task</Button>
                <Button data-testid="submit-log-button" onClick={openReview}>Save &amp; Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {admin && (
          <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
            <DialogContent className="max-w-md" aria-describedby={undefined}>
              <DialogHeader><DialogTitle className="font-display">Unlock Date</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Unlock a previous date so an employee can enter or correct a missed record. This opens their day as LATE and is recorded in the audit history.</p>
                <div><Label>Employee</Label>
                  <Select value={unlockForm.user_id} onValueChange={(v) => setUnlockForm({ ...unlockForm, user_id: v })}>
                    <SelectTrigger data-testid="unlock-user-select" className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{users.filter((u) => u.status === "active").map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Date to unlock</Label><Input data-testid="unlock-date-input" type="date" max={today} value={unlockForm.date} onChange={(e) => setUnlockForm({ ...unlockForm, date: e.target.value })} className="mt-1" /></div>
                <div><Label>Reason</Label><Textarea data-testid="unlock-reason-input" rows={2} value={unlockForm.reason} onChange={(e) => setUnlockForm({ ...unlockForm, reason: e.target.value })} className="mt-1" placeholder="Why is this date being opened?" /></div>
              </div>
              <DialogFooter><Button data-testid="confirm-unlock-button" onClick={unlockDate}>Unlock Date</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {admin && (
          <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
              <DialogHeader><DialogTitle className="font-display">Company Holidays ({holidays.length})</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="rounded-xl border border-border p-3.5 bg-muted/30 space-y-3">
                  <p className="text-xs font-semibold">Add Declared Holiday</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Date</Label><Input data-testid="holiday-date-input" type="date" value={hForm.date} onChange={(e) => setHForm({ ...hForm, date: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs">Holiday Name</Label><Input data-testid="holiday-name-input" value={hForm.name} onChange={(e) => setHForm({ ...hForm, name: e.target.value })} placeholder="e.g. Diwali" className="mt-1" /></div>
                  </div>
                  <Button data-testid="add-holiday-submit" size="sm" onClick={saveHoliday} className="w-full">Add Holiday</Button>
                </div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                  {holidays.map((h) => (
                    <div key={h.date} data-testid={`holiday-row-${h.date}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-xs">
                      <div><span className="font-semibold">{h.name}</span> <span className="text-muted-foreground ml-2">{fmtDate(h.date)}</span></div>
                      <button data-testid={`delete-holiday-${h.date}`} onClick={() => deleteHoliday(h.date)} className="p-1 text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {holidays.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No holidays added.</p>}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {tab === "my" && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex rounded-lg border border-border p-0.5">
              {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([k, lbl]) => (
                <button key={k} data-testid={`my-view-${k}`} onClick={() => setMyView(k)} className={`px-3 py-1.5 text-sm rounded-md ${myView === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{lbl}</button>
              ))}
            </div>
            {myView === "day" && (
              <div className="flex items-center gap-2">
                <Input data-testid="date-picker-input" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40 h-9" />
                <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setSelectedDate(today)}>Today</Button>
              </div>
            )}
          </div>

          {myView === "day" && (
            <>
              {offLabel(selectedDate) && (
                <div className="rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20 px-4 py-3 text-sm flex items-center justify-between">
                  <span>🎉 <strong>{offLabel(selectedDate)}</strong> — Daily Tracker is not required today.</span>
                </div>
              )}

              {/* Day header banner */}
              <div className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">My Day · {fmtDate(selectedDate)}</p>
                  <p className="font-display text-2xl font-bold mt-1">
                    {dayAtt ? (dayAtt.presence === "leave" ? <span className="text-amber-600">On Leave</span> : <>Present · {dayAtt.location}{dayAtt.school ? ` · ${dayAtt.school}` : ""}</>) : "Not Logged Yet"}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <AttendanceBadge s={dayAtt?.attendance_status} />
                    <GpsBadge att={dayAtt} privileged={admin} />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-muted/40 px-3.5 py-2.5 text-center"><p className="font-display font-extrabold text-xl">{daySum.total}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Tasks</p></div>
                  <div className="rounded-xl bg-muted/40 px-3.5 py-2.5 text-center"><p className="font-display font-extrabold text-xl text-emerald-600">{daySum.completed}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Done</p></div>
                  <div className="rounded-xl bg-muted/40 px-3.5 py-2.5 text-center"><p className="font-display font-extrabold text-xl font-mono">{fmtHM(daySum.seconds)}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Active</p></div>
                </div>
              </div>

              {/* Day Activity Table */}
              <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <TableHead showUser={false} />
                  <tbody>
                    {dayActs.length === 0 ? (
                      <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">No activities logged for {fmtDate(selectedDate)}. Click "+ Log Daily Activity &amp; Attendance" to start.</td></tr>
                    ) : (
                      dayActs.map((l) => <LogRow key={l.log_id} l={l} showUser={false} canComplete />)
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {myView === "week" && (() => {
            const wd = weekDaysOf(weekAnchor);
            const rangeLogs = mine.filter((l) => wd.includes(l.date));
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setWeekAnchor(shiftDateStr(weekAnchor, -7))}>← Prev Week</Button>
                    <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setWeekAnchor(today)}>This Week</Button>
                    <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setWeekAnchor(shiftDateStr(weekAnchor, 7))}>Next Week →</Button>
                  </div>
                  <span className="font-display font-semibold text-sm">{fmtDate(wd[0])} – {fmtDate(wd[6])}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-7 gap-2">
                  {wd.map((d) => {
                    const dl = rangeLogs.filter((l) => l.date === d && l.record_type !== "attendance");
                    const isSun = new Date(d + "T00:00:00").getDay() === 0;
                    const isToday = d === today;
                    const hol = holidaySet.has(d) || !!weeklyOffLabel(d, weeklyOffs);
                    return (
                      <div key={d} data-testid={`week-day-${d}`} className={`rounded-xl border p-3 ${hol ? "border-rose-200 dark:border-rose-900 bg-rose-50/40 dark:bg-rose-950/20" : isSun ? "border-rose-200 dark:border-rose-900 bg-rose-50/40 dark:bg-rose-950/20" : "border-border bg-card"} ${isToday ? "ring-1 ring-primary" : ""}`}>
                        <p className={`text-[11px] uppercase tracking-wider ${isSun ? "text-rose-600" : "text-muted-foreground"}`}>{new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}</p>
                        <p className="font-display font-bold text-lg">{new Date(d + "T00:00:00").getDate()}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{hol ? "Holiday" : dl.length === 0 ? "—" : `${dl.length} logged`}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="rounded-xl border border-border bg-card overflow-x-auto">
                  <table className="w-full text-sm min-w-[1000px]"><TableHead showUser={false} />
                    <tbody>{rangeLogs.filter((l) => l.record_type !== "attendance").length === 0 ? <tr><td colSpan={12} className="px-3 py-6 text-center text-muted-foreground">No records this week.</td></tr> : [...rangeLogs].filter((l) => l.record_type !== "attendance").sort((a, b) => (a.date < b.date ? 1 : -1)).map((l) => <LogRow key={l.log_id} l={l} showUser={false} canComplete />)}</tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {myView === "month" && (
            <>
              <div className="flex items-center gap-3">
                <h3 className="font-display font-semibold">Monthly summary</h3>
                <Input data-testid="month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <Stat testid="sum-leave" label="Leave Days" value={sum.leave} />
                <Stat testid="sum-total" label="Total Activities" value={sum.total} />
                <Stat testid="sum-office" label="Office" value={sum.office} />
                <Stat testid="sum-school" label="Field" value={sum.school} />
                <Stat testid="sum-wfh" label="Work From Home" value={sum.wfh} />
                <Stat testid="sum-completed" label="Completed" value={sum.completed} />
                <Stat testid="sum-inprogress" label="In Progress" value={sum.inProgress} />
                <Stat testid="sum-active" label="Active Time" value={fmtHM(sum.seconds)} />
              </div>
              <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]"><TableHead showUser={false} />
                  <tbody>{mine.filter((l) => l.record_type !== "attendance").length === 0 ? <tr><td colSpan={12} className="px-3 py-6 text-center text-muted-foreground">No records this month.</td></tr> : mine.filter((l) => l.record_type !== "attendance").map((l) => <LogRow key={l.log_id} l={l} showUser={false} canComplete />)}</tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {admin && tab === "team-day" && (() => {
        const dayAll = logs.filter((l) => l.date === teamDate);
        const people = Array.from(new Set(dayAll.map((l) => l.user_id))).map((uid) => {
          const att = dayAll.find((l) => l.user_id === uid && l.record_type === "attendance");
          const acts = dayAll.filter((l) => l.user_id === uid && l.record_type !== "attendance");
          const u = users.find((x) => x.user_id === uid);
          return {
            uid,
            name: u?.name || acts[0]?.user_name || att?.user_name || "—",
            att, acts,
            total: acts.length,
            completed: acts.filter((l) => l.status === "completed").length,
            inProg: acts.filter((l) => l.status === "in_progress").length,
            carried: acts.filter((l) => (l.carried_days || 0) > 0).length,
          };
        }).sort((a, b) => a.name.localeCompare(b.name));
        return (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-display font-semibold">Team · Day</h3>
              <Input data-testid="team-date-input" type="date" value={teamDate} onChange={(e) => setTeamDate(e.target.value)} className="w-44 h-9" />
              <span className="font-display font-bold text-lg ml-1">{fmtDate(teamDate)}</span>
            </div>
            {offLabel(teamDate) && <div className="rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20 px-4 py-3 text-sm">🎉 <strong>{offLabel(teamDate)}</strong></div>}
            {people.length === 0 ? (
              <div className="rounded-xl border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">No activity on this date.</div>
            ) : (
              <div className="space-y-4" data-testid="team-day-groups">
                {people.map((p) => {
                  const pct = p.total ? Math.round((p.completed / p.total) * 100) : 0;
                  const onLeave = p.att?.presence === "leave";
                  return (
                    <div key={p.uid} className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden" data-testid={`team-day-emp-${p.uid}`}>
                      <div className="px-5 py-4 border-b border-border bg-muted/30 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-display font-bold text-sm shrink-0" style={{ background: "#e85122" }}>
                            {(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="font-display font-bold truncate" data-testid={`team-day-name-${p.uid}`}>{p.name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {p.att
                                ? onLeave
                                  ? <>Leave{p.att.leave_reason ? ` · ${p.att.leave_reason}` : ""}</>
                                  : <>Present · {p.att.location || "Office"}{p.att.school ? ` · ${p.att.school}` : ""}</>
                                : "Not logged"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <AttendanceBadge s={p.att?.attendance_status} />
                          <GpsBadge att={p.att} privileged={admin} />
                        </div>
                      </div>
                      <div className="px-5 py-3 border-b border-border flex items-center gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-muted-foreground font-semibold">DAILY PROGRESS</span>
                            <span className="font-bold">{pct}% ({p.completed}/{p.total})</span>
                          </div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="px-3 py-1.5 rounded-lg bg-muted/40 text-center"><p className="font-extrabold text-sm">{p.total}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</p></div>
                          <div className="px-3 py-1.5 rounded-lg bg-muted/40 text-center"><p className="font-extrabold text-sm text-emerald-600">{p.completed}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">Done</p></div>
                          <div className="px-3 py-1.5 rounded-lg bg-muted/40 text-center"><p className="font-extrabold text-sm text-blue-600">{p.inProg}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">In Prog</p></div>
                          <div className="px-3 py-1.5 rounded-lg bg-muted/40 text-center"><p className="font-extrabold text-sm text-violet-600">{p.carried}</p><p className="text-[9px] uppercase tracking-wider text-muted-foreground">Carried</p></div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[900px]"><TableHead showUser={false} />
                          <tbody>{p.acts.length === 0 ? <tr><td colSpan={11} className="px-3 py-6 text-center text-muted-foreground">No individual tasks logged.</td></tr> : p.acts.map((l) => <LogRow key={l.log_id} l={l} showUser={false} canComplete={false} />)}</tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      })()}

      {admin && tab === "team-month" && (
        <>
          <div className="flex items-center gap-3">
            <h3 className="font-display font-semibold">Employees Activities for the Month</h3>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" />
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-semibold text-muted-foreground mr-1">Legend:</span>
            {[["O", "Office", "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"],
              ["S", "Field", "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300"],
              ["R", "Remote Work", "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300"],
              ["L", "Leave", "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"],
              ["H", "Holiday", "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300"],
              ["W", "Weekly Off", "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"]].map(([code, label, cls]) => (
              <span key={code} className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5">
                <span className={`h-4 w-4 rounded-full flex items-center justify-center font-bold text-[9px] ${cls}`}>{code}</span>
                <span>{label}</span>
              </span>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="text-xs min-w-max">
              <thead><tr className="border-b border-border bg-muted/40"><th className="sticky left-0 bg-muted/40 text-left font-semibold px-3 py-2">Employee</th>{Array.from({ length: days }, (_, i) => i + 1).map((d) => { const dstr = `${month}-${String(d).padStart(2, "0")}`; const sun = new Date(y, mo - 1, d).getDay() === 0; const hol = holidaySet.has(dstr) || !!weeklyOffLabel(dstr, weeklyOffs); return <th key={d} className={`px-1.5 py-2 font-semibold text-center w-8 ${hol ? "bg-rose-50 dark:bg-rose-950/40 text-rose-600" : sun ? "bg-rose-50 dark:bg-rose-950/40 text-rose-600" : ""}`}><div>{d}</div><div className="text-[8px] font-normal">{new Date(y, mo - 1, d).toLocaleDateString(undefined, { weekday: "narrow" })}</div></th>; })}</tr></thead>
              <tbody>
                {activeUsers.map((u) => (
                  <tr key={u.user_id} data-testid={`grid-row-${u.user_id}`} className="border-b border-border last:border-0">
                    <td className="sticky left-0 bg-card px-3 py-1.5 font-medium whitespace-nowrap">{u.name}</td>
                    {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                      const sun = new Date(y, mo - 1, d).getDay() === 0;
                      const c = cellFor(u.user_id, d);
                      return <td key={d} className={`px-1 py-1 text-center ${sun ? "bg-rose-50/50 dark:bg-rose-950/20" : ""}`}>{c ? <button data-testid={`grid-cell-${u.user_id}-${d}`} onClick={() => setDayDetail({ uid: u.user_id, name: u.name, day: d })} className={`h-6 w-6 rounded text-[10px] font-bold ${cellColor[c]}`}>{c}</button> : <span className="text-muted-foreground">·</span>}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {admin && tab === "team-report" && (
        <div className="space-y-4" data-testid="report-view">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold">Monthly Attendance &amp; Activity</h3>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" data-testid="report-month-input" />
            <Select value={reportFilters.user_id} onValueChange={(v) => setReportFilters({ ...reportFilters, user_id: v })}>
              <SelectTrigger data-testid="report-employee-filter" className="w-44 h-9"><SelectValue placeholder="Employee" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Employees</SelectItem>{activeUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            {departments.length > 0 && (
              <Select value={reportFilters.department} onValueChange={(v) => setReportFilters({ ...reportFilters, department: v })}>
                <SelectTrigger data-testid="report-dept-filter" className="w-44 h-9"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Departments</SelectItem>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={reportFilters.reporting_to} onValueChange={(v) => setReportFilters({ ...reportFilters, reporting_to: v })}>
              <SelectTrigger data-testid="report-rp-filter" className="w-48 h-9"><SelectValue placeholder="Reporting Person" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Reporting Persons</SelectItem>{activeUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="report-export-button" variant="outline" className="h-9 gap-1.5 ml-auto" onClick={exportReport}><Download className="h-4 w-4" /> Export Excel</Button>
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead><tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                {["Employee", "Present", "On Time", "Late", "Leave", "Activities", "Completed", "In Progress", "Carried", "Active Time"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {!report || report.rows.length === 0 ? <tr><td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">No data for this month.</td></tr> : report.rows.map((r) => (
                  <tr key={r.user_id} data-testid={`report-row-${r.user_id}`} className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer" onClick={() => setReportDrill(r)}>
                    <td className="px-3 py-2.5 font-medium">{r.name}<div className="text-[11px] text-muted-foreground font-normal">{r.designation || r.department || ""}</div></td>
                    <td className="px-3 py-2.5">{r.days_present}</td>
                    <td className="px-3 py-2.5 text-emerald-600">{r.days_ontime}</td>
                    <td className="px-3 py-2.5 text-rose-600">{r.days_late}</td>
                    <td className="px-3 py-2.5 text-amber-600">{r.days_leave}</td>
                    <td className="px-3 py-2.5">{r.activities_logged}</td>
                    <td className="px-3 py-2.5">{r.activities_completed}</td>
                    <td className="px-3 py-2.5">{r.activities_inprogress}</td>
                    <td className="px-3 py-2.5 text-violet-600">{r.activities_carried}</td>
                    <td className="px-3 py-2.5 font-mono">{fmtHM(r.total_active_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">Click a row to view that employee's activity detail for the month. Holidays this month: {report?.holidays?.length || 0}.</p>
        </div>
      )}

      {/* Review staged entries before saving */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Review Before Saving</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Nothing is saved yet. Review your entries — edit or remove any — then confirm. Timers start when you confirm.</p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {pending.map((e, i) => (
              <div key={e.id} data-testid={`pending-entry-${i}`} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${e.kind === "leave" ? "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300" : "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300"}`}>{e.kind === "leave" ? "Leave" : "Task"}</span>
                <span className="text-sm flex-1 truncate" title={e.label}>{e.label}</span>
                <span className="text-xs text-muted-foreground shrink-0">{e.payload.date}</span>
                <button data-testid={`pending-edit-${i}`} onClick={() => editEntry(e.id)} className="p-1 text-muted-foreground hover:text-foreground shrink-0" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`pending-remove-${i}`} onClick={() => removeEntry(e.id)} className="p-1 text-muted-foreground hover:text-rose-600 shrink-0" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {pending.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No entries left — go back and add a task or leave.</p>}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Back</Button>
            <Button data-testid="confirm-save-all-button" onClick={confirmSaveAll} disabled={savingAll || pending.length === 0}>
              {savingAll ? "Saving…" : pending.length > 1 ? `Confirm & Save All (${pending.length})` : "Confirm & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity progress: What Was Completed / Outcome / Next Actions (update or complete) */}
      <Dialog open={!!progressLog} onOpenChange={(o) => !o && setProgressLog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-display">{progressMode === "complete" ? "Complete Activity" : "Update Activity"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>What Was Completed?</Label>
              <Textarea data-testid="progress-activity-input" value={progressForm.activity} onChange={(e) => setProgressForm({ ...progressForm, activity: e.target.value })} className="mt-1" rows={2} placeholder="What did you work on?" />
            </div>
            <div>
              <Label>Outcome / What Was Achieved {progressMode === "complete" ? <span className="text-rose-600">*</span> : <span className="text-muted-foreground font-normal">(optional now — required to complete)</span>}</Label>
              <Textarea data-testid="progress-outcome-input" value={progressForm.outcome} onChange={(e) => setProgressForm({ ...progressForm, outcome: e.target.value })} className="mt-1" rows={2} placeholder="Tangible result or milestone achieved" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Next Action <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input data-testid="progress-next-action-input" value={progressForm.next_action} onChange={(e) => setProgressForm({ ...progressForm, next_action: e.target.value })} className="mt-1 text-xs" placeholder="Follow-up step…" />
              </div>
              <div>
                <Label className="text-xs">Next Action Due</Label>
                <Input data-testid="progress-next-action-due-input" type="date" value={progressForm.next_action_due} onChange={(e) => setProgressForm({ ...progressForm, next_action_due: e.target.value })} className="mt-1 text-xs" />
              </div>
            </div>
            {progressMode === "complete" && <p className="text-[11px] text-muted-foreground">The Outcome is required to mark this activity as Completed.</p>}
          </div>
          <DialogFooter>
            {progressMode === "complete"
              ? <Button data-testid="progress-complete-button" onClick={confirmComplete} className="gap-1.5"><CheckCircle2 className="h-4 w-4" /> Mark Completed</Button>
              : <Button data-testid="progress-save-button" onClick={saveProgress} className="gap-1.5">Save</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report drilldown */}
      <Dialog open={!!reportDrill} onOpenChange={(o) => !o && setReportDrill(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{reportDrill?.name} · {month}</DialogTitle></DialogHeader>
          <div className="rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]"><TableHead showUser={false} />
              <tbody>{logs.filter((l) => l.user_id === reportDrill?.user_id && l.record_type !== "attendance").length === 0 ? <tr><td colSpan={12} className="px-3 py-6 text-center text-muted-foreground">No activities.</td></tr> : logs.filter((l) => l.user_id === reportDrill?.user_id && l.record_type !== "attendance").map((l) => <LogRow key={l.log_id} l={l} showUser={false} canComplete={false} />)}</tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dayDetail} onOpenChange={(o) => !o && setDayDetail(null)}>
        <DialogContent className="max-w-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{dayDetail?.name} · {dayDetail && fmtDate(`${month}-${String(dayDetail.day).padStart(2, "0")}`)}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {dayDetail && logs.filter((l) => l.user_id === dayDetail.uid && l.date === `${month}-${String(dayDetail.day).padStart(2, "0")}` && l.record_type !== "attendance").map((l) => (
              <div key={l.log_id} className="rounded-lg border border-border p-3 text-sm">
                <div className="flex items-center justify-between"><span className="font-medium">{l.presence === "leave" ? `Leave · ${l.leave_type}` : (l.activity || l.direct_task_type)}</span><StatusBadge s={l.status} /></div>
                {l.presence !== "leave" && <p className="text-xs text-muted-foreground mt-1">{l.location}{l.school ? ` · ${l.school}` : ""} · {l.task_source === "direct_task" ? `Direct · ${l.direct_task_type}` : (l.linked_asset_label || l.linked_type || "Linked")} · {fmtHM(l.active_seconds)}</p>}
                {(l.outcome || l.leave_reason) && <p className="text-xs mt-1">{l.outcome || l.leave_reason}</p>}
                {l.next_action && <p className="text-[11px] text-primary mt-1">Next: {l.next_action}{l.next_action_due ? ` (due ${fmtDate(l.next_action_due)})` : ""}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
